import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';
import { FormikValues } from 'formik';

import api from '../../../utils/api';
import { GET_SPECIALIZATIONS_QUERY_KEY } from './useCreateSpecializationMutation';

export interface UpdateSpecializationPayload {
  id: number;
  name: string;
  code: string;
  description: string;
  academicProgramId: number;
  activeStatus: boolean;
}

interface SpecializationMutationResponse {
  code: number;
  message: string;
  data?: unknown;
}

const parseBooleanValue = (value: unknown) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }

  return Boolean(value);
};

export const useUpdateSpecializationMutation = (handleClose?: () => void) => {
  const queryClient = useQueryClient();

  const updateSpecialization = async (payload: UpdateSpecializationPayload): Promise<SpecializationMutationResponse> => {
    return (await api.put('/Concentration', payload))?.data;
  };

  const mutation = useMutation({
    mutationKey: ['update-specialization'],
    mutationFn: updateSpecialization,
  });

  const updateSpecializationHandler = async (values: FormikValues, resetForm?: () => void) => {
    const payload: UpdateSpecializationPayload = {
      id: Number(values?.id ?? 0),
      name: values?.name ?? '',
      code: values?.code ?? '',
      description: values?.description ?? '',
      academicProgramId: Number(values?.academicProgramId ?? values?.academicProgram ?? 0),
      activeStatus: parseBooleanValue(values?.activeStatus ?? values?.status ?? true),
    };

    try {
      await mutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: data?.message || 'Specialization updated successfully',
          });

          queryClient.refetchQueries({
            queryKey: GET_SPECIALIZATIONS_QUERY_KEY,
          });

          resetForm?.();
          handleClose?.();
        },
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || error?.response?.data?.title || 'Failed to update specialization',
      });
    }
  };

  return {
    updateSpecializationHandler,
    isUpdateLoading: mutation.isPending,
  };
};
